import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  PeppolSmpProvider,
  PeppolTransactionProvider,
  ProviderConnectivity,
  ProviderRegion,
  Transaction,
} from '@solara/peppol-provider';
import { createMockTickstarBundle } from './mock';

/**
 * Singleton React context that hands out the TickStar Transaction + SMP
 * providers to the customer portal and admin console.
 *
 * In production this provider would be wired to a backend BFF; in the demo
 * it uses the in-memory mock from `@solara/peppol-tickstar/mock` so the UI
 * has working flows for invoice send / participant registration / lookup.
 *
 * To switch a single app to "live" mode in the future, pass
 * `transactions={new LiveTickstarTransactionProvider(...)}` and `smp={...}`
 * to <PeppolGatewayProvider/>.
 */
export interface PeppolGateway {
  transactions: PeppolTransactionProvider;
  smp: PeppolSmpProvider;
  connectivity: ProviderConnectivity;
}

const PeppolGatewayContext = createContext<PeppolGateway | null>(null);

export interface PeppolGatewayProviderProps {
  region?: ProviderRegion;
  /** Optional override — when present, replaces the mock providers. */
  transactions?: PeppolTransactionProvider;
  smp?: PeppolSmpProvider;
  children: ReactNode;
}

/**
 * One stable mock bundle per browser session — ensures that sending an
 * invoice on /new shows up on /invoices and on the admin /tickstar transactions
 * tab, because every component reads from the same in-memory store.
 */
let __sharedMockBundle: ReturnType<typeof createMockTickstarBundle> | undefined;
function getSharedMockBundle(region: ProviderRegion) {
  if (!__sharedMockBundle) {
    __sharedMockBundle = createMockTickstarBundle(region);
  }
  return __sharedMockBundle;
}

export function PeppolGatewayProvider({
  region = 'EU',
  transactions,
  smp,
  children,
}: PeppolGatewayProviderProps) {
  const value = useMemo<PeppolGateway>(() => {
    if (transactions && smp) {
      return {
        transactions,
        smp,
        connectivity: transactions.connectivity(),
      };
    }
    const bundle = getSharedMockBundle(region);
    return {
      transactions: bundle.transactions,
      smp: bundle.smp,
      connectivity: bundle.transactions.connectivity(),
    };
  }, [region, transactions, smp]);

  return (
    <PeppolGatewayContext.Provider value={value}>{children}</PeppolGatewayContext.Provider>
  );
}

export function usePeppolGateway(): PeppolGateway {
  const ctx = useContext(PeppolGatewayContext);
  if (!ctx) {
    throw new Error('usePeppolGateway must be used inside <PeppolGatewayProvider>');
  }
  return ctx;
}

export function usePeppolTransactionProvider(): PeppolTransactionProvider {
  return usePeppolGateway().transactions;
}

export function usePeppolSmpProvider(): PeppolSmpProvider {
  return usePeppolGateway().smp;
}

export function usePeppolConnectivity(): ProviderConnectivity {
  return usePeppolGateway().connectivity;
}

// ---------------------------------------------------------------
// Convenience: subscribe to transaction state. Used by the customer portal's
// invoice detail page to live-update PROCESSING → COMPLETED. The mock
// provider auto-transitions after ~1.2 s; in production this would be a
// SSE/Socket.IO stream.
// ---------------------------------------------------------------

export function useTransaction(transactionId: string | undefined, pollMs = 800): {
  transaction: Transaction | undefined;
  loading: boolean;
  error: Error | undefined;
} {
  const provider = usePeppolTransactionProvider();
  const [transaction, setTransaction] = useState<Transaction | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!transactionId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        setLoading(true);
        const tx = await provider.getTransaction(transactionId);
        if (cancelled) return;
        setTransaction(tx);
        setError(undefined);
        // Stop polling once we've reached a terminal state.
        if (tx.state !== 'COMPLETED' && tx.state !== 'ACKNOWLEDGED' && tx.state !== 'PURGED') {
          timer = setTimeout(tick, pollMs);
        }
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [provider, transactionId, pollMs]);

  return { transaction, loading, error };
}
